// src/pages/platform/AdminsPage.tsx
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShieldPlus, ShieldMinus, Shield } from 'lucide-react';
import { apiClient } from '@/lib/api';
import Button from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';

interface AdminUser { user_id: string; email: string; role: string; }

const AdminsPage: React.FC = () => {
  const qc = useQueryClient();
  const { data: admins = [], isLoading } = useQuery<AdminUser[]>({
    queryKey: ['platform-admins'],
    queryFn: () => apiClient.get('/platform/admins').then(r => r.data),
  });

  const promote = useMutation({
    mutationFn: (userId: string) => apiClient.post(`/platform/admins/${userId}/promote`),
    onSuccess: () => { toast.success('Promoted to admin'); qc.invalidateQueries({ queryKey: ['platform-admins'] }); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Failed'),
  });

  const demote = useMutation({
    mutationFn: (userId: string) => apiClient.delete(`/platform/admins/${userId}`),
    onSuccess: () => { toast.success('Admin role removed'); qc.invalidateQueries({ queryKey: ['platform-admins'] }); },
    onError: (e: any) => toast.error(e.response?.data?.error ?? 'Failed'),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Admins" description="Who can run the platform. Promote an admin to super admin, or remove the admin role." />

      <Panel title="Platform admins" bodyClassName="px-0 pb-2">
        {isLoading ? (
          <TableSkeleton rows={3} />
        ) : admins.length === 0 ? (
          <EmptyState icon={<Shield />} title="No admins yet." />
        ) : (
          <Table minWidth={440}>
            <THead>
              <tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th><span className="sr-only">Actions</span></Th>
              </tr>
            </THead>
            <TBody>
              {admins.map(a => (
                <Tr key={a.user_id}>
                  <Td>{a.email}</Td>
                  <Td>
                    <Badge tone={a.role === 'super_admin' ? 'accent' : 'neutral'} className="font-mono">{a.role}</Badge>
                  </Td>
                  <Td align="right">
                    {a.role !== 'super_admin' && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" title="Promote to super_admin" aria-label="Promote to super_admin" onClick={() => promote.mutate(a.user_id)}><ShieldPlus className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" title="Remove admin" aria-label="Remove admin" className="hover:text-error" onClick={() => demote.mutate(a.user_id)}><ShieldMinus className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>
    </div>
  );
};

export default AdminsPage;
